// Minimal API client for Smart Gymnastics publikumsapp

const PRODUCTION = 'https://competitions.smartgymnastics.no/api/app';

// Configuration precedence:
// 1. SG_BASE_URL (full URL, e.g. "http://192.168.1.224:8000/api/app" or "https://...")
// 2. SG_HOST + optional SG_PORT (host may be '192.168.1.224' or 'http://192.168.1.224')
// 3. PRODUCTION
const envBase = (process as any).env?.SG_BASE_URL;
const envHost = (process as any).env?.SG_HOST;
const envPort = (process as any).env?.SG_PORT;

function buildFromHost(host?: string | undefined, port?: string | undefined) {
  if (!host) return null;
  const normalizedHost = host.startsWith('http') ? host.replace(/\/$/, '') : `http://${host.replace(/\/$/, '')}`;
  const hostWithPort = port ? `${normalizedHost}:${port}` : normalizedHost;
  return `${hostWithPort.replace(/\/$/, '')}/api/app`;
}

const BASE_URL = envBase || buildFromHost(envHost, envPort) || PRODUCTION;

// Base URL without the /api/app suffix, for media files
const MEDIA_BASE = BASE_URL.replace(/\/api\/app\/?$/, '');

export function url(path: string) {
  return `${BASE_URL}${path}`;
}

/** Use for image_url fields that are server-relative paths (e.g. /media/...) */
export function mediaUrl(path: string) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${MEDIA_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
}

export async function get<T>(path: string) {
  const res = await fetch(url(path));
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

export async function post<T>(path: string, body?: unknown, sessionId?: string) {
  const headers: any = { 'Content-Type': 'application/json' };
  if (sessionId) headers['X-Session-Id'] = sessionId;
  const res = await fetch(url(path), {
    method: 'POST',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text();
    let errMsg = text;
    try { errMsg = JSON.parse(text).error || text; } catch(e){}
    throw new Error(`POST ${path} failed: ${res.status} ${errMsg}`);
  }
  return (await res.json()) as T;
}

export type EventType =
  | 'first_open'
  | 'app_open'
  | 'competitions_list'
  | 'competition_view'
  | 'schedule_view'
  | 'kiosk_view'
  | 'current_run_view'
  | 'heart_sent'
  | 'deals_list'
  | 'hearts_leaderboard_view';

export interface LogEventParams {
  event_type: EventType;
  competition_id?: number | null;
  run_id?: number | null;
  metadata?: {
    platform?: string;
    app_version?: string;
    screen?: string;
    action?: string;
  };
}

export async function logEvent(params: LogEventParams, sessionId: string): Promise<void> {
  try {
    await post<{ ok: boolean }>('/events', {
      event_type: params.event_type,
      competition_id: params.competition_id ?? null,
      run_id: params.run_id ?? null,
      metadata: params.metadata ?? {},
    }, sessionId);
  } catch {
    // Best-effort – never throw on analytics failures
  }
}
